/**
 * storage-namespace.js — SSOT de prefijos de localStorage / IndexedDB por brand.
 *
 * Uso:
 *   import { key, keyDash, dbName } from '@shared/config/storage-namespace.js';
 *   localStorage.getItem(key('device_id'))         // → 'loterianacional_device_id'
 *   localStorage.getItem(keyDash('cashier-lang'))  // → 'loterianacional-cashier-lang'
 *   indexedDB.open(dbName('player_history'))       // → 'loterianacional_player_history'
 *
 * El prefijo viene de import.meta.env.VITE_STORAGE_PREFIX, que el generador
 * (scripts/generate-env.js) emite desde infra/brands/<id>.json → storagePrefix.
 *
 * Migración automática:
 *   La primera vez que este módulo se evalúa en un origen `loterianacional_*`,
 *   copia las keys viejas con prefijo `loto37_*` o `loto37-*` (y la key especial
 *   `loto_skeleton_snapshots`) al prefijo nuevo. Idempotente — usa una key flag
 *   para no repetir. Las keys viejas NO se borran (rollback safety).
 *
 *   IndexedDBs (`loto37_player_history`, `loto37_bet_history`) NO se migran:
 *   son cachés rehidratables desde backend. El primer boot crea DB nueva vacía.
 *
 * Garantía de orden:
 *   La migración corre top-level, cuando se evalúa el módulo. Cualquier importador
 *   que use key()/keyDash()/dbName() necesariamente corre después. Por eso este
 *   módulo debe importarse desde el archivo de configuración del front (app-config,
 *   cashier-config, etc.) ANTES de cualquier acceso a localStorage.
 */

import {
    traducirKeyDeStoragePrefijoLoto37,
    ejecutarMigracionDeKeysSiCorresponde,
} from './storage-namespace-migration-logic.js';

const STORAGE_PREFIX_BRUTO = import.meta.env.VITE_STORAGE_PREFIX;

if (!STORAGE_PREFIX_BRUTO || typeof STORAGE_PREFIX_BRUTO !== 'string' || STORAGE_PREFIX_BRUTO.trim() === '') {
    throw new Error(
        '[storage-namespace] VITE_STORAGE_PREFIX obligatorio en .env del build. ' +
            'Generar con: node scripts/generate-env.js --brand=<id> --env=<entorno> --write'
    );
}

// Trim defensivo: si el .env trae espacios accidentales en el valor, evita keys
// con espacios (ej. ` loterianacional _device_id`) que serían imposibles de leer
// luego con la version sin espacios.
const STORAGE_PREFIX = STORAGE_PREFIX_BRUTO.trim();

// ─────────────────────────────────────────────────────────────────────────────
// Migración top-level (corre una sola vez al evaluar el módulo)
// ─────────────────────────────────────────────────────────────────────────────

if (typeof localStorage !== 'undefined') {
    try {
        ejecutarMigracionDeKeysSiCorresponde(localStorage, STORAGE_PREFIX);
    } catch (errorMigracion) {
        // Storage lleno, modo privado bloqueando, etc. Si la migración falla,
        // el usuario empieza con storage limpio — no es regresión catastrófica.
        console.warn('[storage-namespace] migración loto37 → ' + STORAGE_PREFIX + ' falló:', errorMigracion);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// API pública — constructores de keys con prefijo de la brand
// ─────────────────────────────────────────────────────────────────────────────

/** Construye key estilo `loterianacional_<sufijo>`. */
export function key(sufijo) {
    return `${STORAGE_PREFIX}_${sufijo}`;
}

/** Construye key estilo `loterianacional-<sufijo>` (legado: i18n usaba guión). */
export function keyDash(sufijo) {
    return `${STORAGE_PREFIX}-${sufijo}`;
}

/** Construye nombre de IndexedDB estilo `loterianacional_<nombre>`. */
export function dbName(nombre) {
    return `${STORAGE_PREFIX}_${nombre}`;
}

/** Prefijo crudo, exportado para casos especiales (ej. compose con sufijo dinámico). */
export { STORAGE_PREFIX };

// Re-export para que tests y consumidores avanzados puedan invocar la lógica pura.
export { traducirKeyDeStoragePrefijoLoto37, ejecutarMigracionDeKeysSiCorresponde };
